// ─────────────────────────────────────────────
// KLOTHIQ — Delete Sale Confirmation Modal
// Soft-deletes sale & moves to Recycle Bin
// ─────────────────────────────────────────────

import UserGuide from '@/components/help/UserGuide';
import { Input } from '@/components/ui/FormControls';
import { useState } from 'react';
import { useSoftDeleteSale } from '@/hooks/useSales';
import { toast } from 'sonner';
import { Loader2, X, AlertTriangle, Trash2 } from 'lucide-react';

export interface DeleteSaleModalProps {
  isOpen: boolean;
  onClose: () => void;
  saleId: string;
  saleNumber: string;
  totalAmount: number;
  onSuccess?: () => void;
}

export default function DeleteSaleModal({
  isOpen,
  onClose,
  saleId,
  saleNumber,
  totalAmount,
  onSuccess,
}: DeleteSaleModalProps) {
  const softDeleteSale = useSoftDeleteSale();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleDelete = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await softDeleteSale.mutateAsync({
        sale_id: saleId,
        reason: reason.trim() || 'Moved to recycle bin',
      });
      toast.success(`Sale ${saleNumber} moved to Recycle Bin`);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err: unknown) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to delete sale order');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm" data-field-guide-scope>
      <div className="obsidian-panel w-full max-w-md rounded-lg border border-destructive/30 bg-card p-6 shadow-2xl">
        <div className="mb-4 flex items-center justify-between border-b border-border pb-3">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <div className="flex items-start justify-between gap-3 pr-8">
              <h3 className="text-base font-bold text-foreground">Move Sale to Recycle Bin?</h3>
              <UserGuide compact />
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Warning Banner */}
        <div className="mb-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-xs text-destructive">
          <p className="font-semibold text-foreground">
            Warning: You are deleting Sale Order <span className="font-mono text-primary">{saleNumber}</span> (₹{totalAmount.toLocaleString('en-IN')})
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-4 text-muted-foreground">
            <li>Line items & associated payments will be archived.</li>
            <li>Stock reservations will be canceled and delivered stock will be restored to inventory on-hand.</li>
            <li>This order can be restored or permanently purged from the <strong>Recycle Bin</strong> page.</li>
          </ul>
        </div>

        <form onSubmit={handleDelete} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Reason for Deletion (Optional)</label>
            <Input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Customer canceled order / Data entry error"
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              helpLabel="Reason for Deletion (Optional)"
            />
          </div>

          <div className="mt-6 flex justify-end gap-2 border-t border-border pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border bg-background px-4 py-2 text-xs font-medium text-foreground hover:bg-accent"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center gap-2 rounded-md bg-destructive px-4 py-2 text-xs font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-60"
            >
              {isSubmitting ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Trash2 className="h-3.5 w-3.5" />
              )}
              Move to Recycle Bin
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
